import {
  ModuleWithProviders,
  EnvironmentProviders,
  importProvidersFrom,
  Provider,
  inject,
  Optional,
  PLATFORM_ID,
  REQUEST_CONTEXT,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
// import { FacebookPixelModule } from '@padspin/ui-facebook-pixel';
import {
  FirebaseApp,
  initializeApp,
  initializeServerApp,
  provideFirebaseApp,
} from '@angular/fire/app';
import { webEnvironment } from '@padspin/ui-environment';
import {
  connectFunctionsEmulator,
  getFunctions,
  provideFunctions,
} from '@angular/fire/functions';
import {
  connectFirestoreEmulator,
  getFirestore,
  provideFirestore,
} from '@angular/fire/firestore';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  connectAuthEmulator,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  provideAuth,
} from '@angular/fire/auth';
import {
  connectStorageEmulator,
  getStorage,
  provideStorage,
} from '@angular/fire/storage';
import {
  getRemoteConfig,
  provideRemoteConfig,
} from '@angular/fire/remote-config';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { provideNotificationPermissionEnforcer } from '@padspin/ui-push-notifications';
import { provideAnalytics } from '@angular/fire/analytics';
import {
  provideAppCheck,
  initializeAppCheck,
  ReCaptchaV3Provider,
  CustomProvider,
} from '@angular/fire/app-check';
import { getAnalyticsForWeb } from './get-analytics-for-web';
import {
  provideSessionCookie,
  serverRenderAuthIdToken,
} from './session-cookie';
import {
  provideGoogleMapsJsApiLoader,
  provideMapLoadingService,
} from '@padspin/ui-mapping';
import { provideOptimus } from '@openng/optimus-ui/config';
import {
  padspinPrimengConfig,
  providePrimengDarkModeToggler,
} from '@padspin/ui-styles';
import { environment } from '@padspin/ui-environment';
import { FIREBASE_ADMIN } from '@padspin/ui-firebase-admin';
import { provideProfileClaimsWatcher } from '@padspin/ui-profile/profile-claims-watcher';

const globalModulesWithProviders: ModuleWithProviders<unknown>[] = [
  // The Meta pixel (~220 KB of script) is off to speed up page loads; restore
  // this and its import to turn it back on.
  // FacebookPixelModule.forRoot({ enabled: true, pixelId: '311269089285285' }),
];

/**
 * The browser's app, or on the server, an app signed in as the visitor whose
 * ID token `server.ts` passed in the request context. Every Firebase provider
 * must take this app: one that resolves the default app renders signed out.
 */
export const firebaseAppProvider: EnvironmentProviders[] = [
  provideFirebaseApp(() => {
    if (isPlatformBrowser(inject(PLATFORM_ID))) {
      return initializeApp(webEnvironment.firebase);
    }
    const requestContext = inject(REQUEST_CONTEXT, { optional: true });
    const authIdToken = serverRenderAuthIdToken(requestContext);
    if (!authIdToken || !requestContext) {
      return initializeApp(webEnvironment.firebase);
    }
    return initializeServerApp(webEnvironment.firebase, {
      authIdToken,
      releaseOnDeref: requestContext,
    });
  }),
  provideSessionCookie(),
];
export const firebaseFirestoreProvider: EnvironmentProviders[] = [
  provideFirestore(() => {
    const firestore = getFirestore(inject(FirebaseApp));
    if (environment.useEmulators) {
      connectFirestoreEmulator(firestore, 'localhost', 8080);
    }
    return firestore;
  }),
];

const applicationProviders: Provider[] = [];
const applicationEnvironmentProviders: EnvironmentProviders[] = [
  provideMapLoadingService(),
  provideGoogleMapsJsApiLoader({
    apiKey: environment.googleMapsApiKey,
    libraries: ['places', 'geometry'],
    version: 'weekly',
  }),
  importProvidersFrom(globalModulesWithProviders),
  ...firebaseAppProvider,
  provideFunctions(() => {
    const functions = getFunctions(inject(FirebaseApp));
    if (environment.useEmulators) {
      connectFunctionsEmulator(functions, 'localhost', 5001);
    }
    return functions;
  }),
  ...firebaseFirestoreProvider,
  provideAuth(() => {
    // getAuth() also installs the popup/redirect resolver, which loads Google's
    // sign-in iframe on every page; nothing signs in with a popup or redirect.
    const auth = isPlatformBrowser(inject(PLATFORM_ID))
      ? initializeAuth(inject(FirebaseApp), {
          persistence: [
            indexedDBLocalPersistence,
            browserLocalPersistence,
            browserSessionPersistence,
          ],
        })
      : getAuth(inject(FirebaseApp));
    if (environment.useEmulators) {
      connectAuthEmulator(auth, 'http://127.0.0.1:9099', {
        disableWarnings: true,
      });
    }
    return auth;
  }),
  provideStorage(() => {
    const storage = getStorage(inject(FirebaseApp));
    if (environment.useEmulators) {
      connectStorageEmulator(storage, 'localhost', 9199);
    }
    return storage;
  }),
  provideRemoteConfig(() => getRemoteConfig(inject(FirebaseApp))),
  provideMessaging(() => getMessaging(inject(FirebaseApp))),
  provideNotificationPermissionEnforcer(),
  provideAnalytics(() => getAnalyticsForWeb()),
  provideAppCheck(() => {
    // @angular/fire/app-check only works on the browser, so we have to use
    // the firebase-admin SDk for server and pre-rendered routes.
    const admin = inject(FIREBASE_ADMIN, { optional: true });
    const appId: string | undefined = environment.firebase?.appId;
    if (!appId) {
      throw new Error('Firebase App ID is not defined in the environment.');
    }
    if (environment.useEmulators && typeof self === 'object' && self !== null) {
      Object.assign(self, {
        FIREBASE_APPCHECK_DEBUG_TOKEN:
          environment.firebaseAppcheckDebugToken ?? true,
      });
    }
    if (admin) {
      const provider = new CustomProvider({
        getToken: () =>
          admin
            .appCheck()
            .createToken(appId, {
              ttlMillis: 604_800_000 /* One week */,
            })
            .then(
              ({ token, ttlMillis }: { token: string; ttlMillis: number }) => ({
                token,
                expireTimeMillis: Date.now() + ttlMillis,
              }),
            ),
      });
      return initializeAppCheck(inject(FirebaseApp), {
        provider,
        isTokenAutoRefreshEnabled: false,
      });
    } else {
      const provider = new ReCaptchaV3Provider(
        '6LfYDl8rAAAAAPhuvIS95penidR4hw4m4LpgFDAp',
      );
      return initializeAppCheck(inject(FirebaseApp), {
        provider,
        isTokenAutoRefreshEnabled: true,
      });
    }
  }, [new Optional(), FIREBASE_ADMIN]),
  provideOptimus(padspinPrimengConfig),
  providePrimengDarkModeToggler(),
  provideProfileClaimsWatcher(),
];

/**
 * Global providers to be included in the application configuration:
 *@example
 * ```typescript
 * import { applicationConfigProviders } from '@padspin/web';
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideClientHydration(),
 *     ...applicationConfigProviders,
 *   ],
 * };
 * ```
 */
export const applicationConfigProviders: (Provider | EnvironmentProviders)[] = [
  ...applicationProviders,
  ...applicationEnvironmentProviders,
];
